import { Component, ChangeDetectionStrategy, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { FooterComponent } from '../shared/footer/footer';
import { environment } from '../../environments/environment';
import { TranslationService, Language } from '../services/translation.service';
import { PortalUiService } from '../services/portal-ui.service';
import { resolveSchoolFromUrl, getSchoolDisplayName } from '../core/config/school-logos.config';

type AuthMode = 'login' | 'register' | 'forgot-password' | 'reset-password';

@Component({
    selector: 'app-ext-auth',
    templateUrl: './ext-auth.html',
    standalone: true,
    imports: [FormsModule, CommonModule, HttpClientModule, FooterComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExtAuth implements OnInit {
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly http = inject(HttpClient);
    readonly ts = inject(TranslationService);
    private readonly portalUi = inject(PortalUiService);

    // Dynamic API Base URL detection
    public readonly apiBase = `${environment.extAuthUrl}/ext-auth`;

    mode = signal<AuthMode>('login');
    isLoading = signal(false);
    message = signal('');
    error = signal('');
    retryTimer = signal(0);

    // Detected school name for OpenOlat welcome banner
    schoolName = signal<string | null>(null);

    clientId = signal('');
    redirectUri = signal('');
    scope = signal('');
    state = signal('');
    nonce = signal('');
    responseType = signal('');

    // Form fields
    username = signal('');
    password = signal('');
    email = signal('');
    registrationCode = signal('');
    resetCode = signal('');
    newPassword = signal('');

    languages: Language[] = ['de', 'en', 'fr', 'it', 'nl'];

    ngOnInit(): void {
        this.route.queryParams.subscribe(params => {
            // Default client_id to 'openolat-client'
            this.clientId.set(params['client_id'] || 'openolat-client');

            // Logic for redirect_uri: param -> referrer -> default
            let redirect = params['redirect_uri'];
            if (!redirect) {
                // Try to use referrer as origin, fallback to default
                const referrer = document.referrer;
                // Use lms-test as default per user request
                redirect = referrer ? referrer : 'https://lms-test.vantage.ch';
            }
            this.redirectUri.set(redirect);

            // Auto-detect school from OpenOlat redirect_uri / referrer
            const schoolMatch = resolveSchoolFromUrl(redirect) || resolveSchoolFromUrl(params['school']) || resolveSchoolFromUrl(document.referrer);
            if (schoolMatch) {
                this.portalUi.setActiveSchools(schoolMatch);
                this.schoolName.set(getSchoolDisplayName(schoolMatch));
            } else {
                this.portalUi.setActiveSchools(null);
                this.schoolName.set(null);
            }

            // Default scope to openid profile email
            this.scope.set(params['scope'] || 'openid profile email');
            this.state.set(params['state'] || '');
            this.nonce.set(params['nonce'] || '');
            this.responseType.set(params['response_type'] || '');

            // Detect errors from backend redirect
            if (params['error'] === 'invalid_password') {
                this.error.set(this.ts.t('msg_password_wrong'));
                this.startRetryTimer();
            } else if (params['error'] === 'user_not_found') {
                this.error.set(this.ts.t('msg_username_unknown'));
                this.startRetryTimer();
            } else if (params['error'] === 'invalid_credentials') {
                this.error.set(this.ts.t('msg_login_failed'));
                this.startRetryTimer();
            }

            // Set language if provided
            if (params['lang']) {
                this.ts.setLanguage(params['lang'] as Language);
            }

            // Detect mode from query params
            if (params['code']) {
                if (params['mode'] === 'register') {
                    this.mode.set('register');
                    this.registrationCode.set(params['code']);
                } else {
                    this.mode.set('reset-password');
                    this.resetCode.set(params['code']);
                }
            }
        });
    }

    private startRetryTimer(): void {
        this.retryTimer.set(5);
        const interval = setInterval(() => {
            this.retryTimer.update(val => val - 1);
            if (this.retryTimer() <= 0) {
                clearInterval(interval);
            }
        }, 1000);
    }

    setMode(mode: string): void {
        this.mode.set(mode as AuthMode);
        this.error.set('');
        this.message.set('');
        this.retryTimer.set(0);
    }

    setLanguage(lang: Language): void {
        console.log('🚀 ExtAuth.setLanguage:', lang);
        this.ts.setLanguage(lang);
    }

    onRequestRegistration() {
        this.isLoading.set(true);
        this.http.post(`${this.apiBase}/request-registration`, {
            email: this.email(),
            fk_contact_id: 1 // Placeholder
        }, { params: { email: this.email(), fk_contact_id: '1' } }).subscribe({
            next: (res: any) => {
                this.message.set('Registration link sent to your email.');
                this.isLoading.set(false);
            },
            error: (err) => {
                this.error.set('Failed to request registration.');
                this.isLoading.set(false);
            }
        });
    }

    onRegister() {
        this.isLoading.set(true);
        this.http.post(`${this.apiBase}/register`, {
            registration_code: this.registrationCode(),
            password: this.password()
        }).subscribe({
            next: (res: any) => {
                const successMsg = 'Account activated! You can now login.';
                this.message.set(successMsg);
                this.isLoading.set(false);
                // Redirect to the main portal login page with success message
                this.router.navigate(['/login'], { queryParams: { msg: successMsg } });
            },
            error: (err) => {
                this.error.set('Registration failed. Check your code.');
                this.isLoading.set(false);
            }
        });
    }

    onForgotPassword() {
        this.isLoading.set(true);
        this.http.post(`${this.apiBase}/forgot-password`, {
            userid: this.email()
        }).subscribe({
            next: (res: any) => {
                this.message.set('If the account exists, a reset link was sent.');
                this.isLoading.set(false);
            },
            error: (err) => {
                this.error.set('Something went wrong.');
                this.isLoading.set(false);
            }
        });
    }

    onResetPassword() {
        this.isLoading.set(true);
        this.http.post(`${this.apiBase}/reset-password`, {
            password_reset_code: this.resetCode(),
            new_password: this.newPassword()
        }).subscribe({
            next: (res: any) => {
                const successMsg = 'Password reset successful. You can now login.';
                this.message.set(successMsg);
                this.isLoading.set(false);
                // Redirect to the main portal login page with success message
                this.router.navigate(['/login'], { queryParams: { msg: successMsg } });
            },
            error: (err) => {
                this.error.set('Failed to reset password.');
                this.isLoading.set(false);
            }
        });
    }
}
